import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../store/authContext';
import { dashboardApi } from '../../services/api';
import logger from '../../utils/logger';
import SuperAdminDashboard from './SuperAdminDashboard';
import './Dashboard.css';

/* ─── Presentation constants ───────────────────────────────────────────── */
const SOURCE_COLORS = {
  '99acres':     '#D4A853',
  'MagicBricks': '#5B9BD5',
  'Housing.com': '#9B72CF',
  'Manual':      '#3CB371',
};

const STAGE_COLORS = {
  'New':         'var(--info)',
  'Contacted':   'var(--warning)',
  'Site Visit':  'var(--purple)',
  'Negotiation': 'var(--accent)',
  'Closed':      'var(--success)',
  'Lost':        'var(--danger)',
};

/* ─── Dashboard ─────────────────────────────────────────────────────────── */

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      setError('');
      const res = await dashboardApi.stats();
      setStats(res.data);
    } catch (err) {
      logger.error('api', 'Failed to load dashboard stats', { error: err.message });
      setError('Failed to load dashboard stats.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      fetchStats();
    } else {
      setLoading(false);
    }
  }, [fetchStats, user?.role]);

  if (user?.role === 'super_admin') {
    return <SuperAdminDashboard />;
  }

  if (loading) {
    return (
      <div className="dashboard">
        <div className="db-loading">
          <div className="db-loading-spinner" />
          <p>Loading dashboard…</p>
        </div>
      </div>
    );
  }

  const greeting = getGreeting();
  const maxStageCount = stats
    ? Math.max(...stats.pipeline.map(s => s.count), 1)
    : 1;

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="page-header animate-fade-up">
        <div className="page-header-top">
          <div>
            <p className="dashboard-greeting">{greeting}</p>
            <h1 className="page-title">{user?.name || 'Dashboard'}</h1>
          </div>
          <div className="page-actions">
            <Link to="/leads" className="btn btn-ghost btn-sm">View all leads</Link>
            <Link to="/leads/new" className="btn btn-primary btn-sm">
              <PlusIcon /> Add lead
            </Link>
          </div>
        </div>
        <p className="page-subtitle">
          {formatDate(new Date())} &middot; {user?.tenantName || 'Your workspace'}
        </p>
      </div>

      {error && <div className="db-error animate-fade-up">{error}</div>}

      {stats && (
        <>
          {/* Top row: stat cards + source breakdown */}
          <div className="dashboard-top stagger">
            <StatCard
              label="Total Leads"
              value={stats.total}
              icon={<LeadsIcon />}
              className="animate-fade-up"
            />
            <StatCard
              label="New Today"
              value={stats.new_today}
              icon={<SparkIcon />}
              accent
              className="animate-fade-up"
            />

            {/* Quick actions */}
            <div className="card card-sm db-quick-card animate-fade-up">
              <div className="stat-card-label" style={{ marginBottom: 12 }}>Quick Actions</div>
              <div className="quick-actions">
                {user?.role === 'agent' ? (
                  <Link to="/leads" className="quick-action">
                    <ClockIcon />
                    <span>Needs follow-up</span>
                    <span className="quick-action-badge">{stats.follow_up_count}</span>
                  </Link>
                ) : (
                  <Link to="/leads?filter=unassigned" className="quick-action">
                    <AlertIcon />
                    <span>Unassigned leads</span>
                    <span className="quick-action-badge">{stats.unassigned_count}</span>
                  </Link>
                )}
              </div>
            </div>

            {/* Source breakdown card */}
            <div className="card db-source-card animate-fade-up">
              <div className="stat-card-label" style={{ marginBottom: 14 }}>Lead Sources</div>
              <div className="source-bars">
                {stats.sources.map((s) => (
                  <div key={s.source} className="source-bar-row">
                    <div className="source-bar-label">
                      <span className="source-bar-name">{s.source}</span>
                      <span className="source-bar-count">{s.count}</span>
                    </div>
                    <div className="source-bar-track">
                      <div
                        className="source-bar-fill"
                        style={{ width: `${s.pct}%`, background: SOURCE_COLORS[s.source] || '#888' }}
                      />
                    </div>
                    <span className="source-bar-pct">{s.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main grid: recent leads table + pipeline/quick-actions */}
          <div className="dashboard-grid animate-fade-up" style={{ animationDelay: '160ms' }}>
            {/* Recent leads */}
            <div className="card dashboard-recent">
              <div className="card-head">
                <div>
                  <h3 className="card-title">Recent Leads</h3>
                  <p className="card-desc">Latest incoming from all portals</p>
                </div>
                <Link to="/leads" className="btn btn-ghost btn-sm">See all</Link>
              </div>
              <div className="recent-table-wrap">
                <table className="recent-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Source</th>
                      <th>Stage</th>
                      <th>Budget</th>
                      <th>Assigned</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_leads.map((lead) => (
                      <tr key={lead.id}>
                        <td>
                          <Link to={`/leads/${lead.id}`} className="lead-name-link">
                            <div className="lead-avatar-sm">
                              {lead.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                            </div>
                            <div>
                              <div className="lead-name">{lead.name}</div>
                              <div className="lead-phone">{lead.phone}</div>
                            </div>
                          </Link>
                        </td>
                        <td><span className="source-badge">{lead.source}</span></td>
                        <td><StageBadge stage={lead.stage} /></td>
                        <td><span className="lead-budget">{lead.budget || '—'}</span></td>
                        <td>
                          {lead.assigned_to_name
                            ? <span className="lead-agent">{lead.assigned_to_name}</span>
                            : <span className="lead-unassigned">Unassigned</span>}
                        </td>
                        <td className="lead-time">{timeAgo(lead.created_at)}</td>
                      </tr>
                    ))}
                    {stats.recent_leads.length === 0 && (
                      <tr>
                        <td colSpan="6" className="recent-empty">No leads yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pipeline funnel */}
            <div className="card">
              <div className="card-head">
                <h3 className="card-title">Pipeline</h3>
                <p className="card-desc">Current stage distribution</p>
              </div>
              <div className="funnel-list">
                {stats.pipeline.map((s, i) => (
                  <div key={s.stage} className="funnel-row">
                    <div className="funnel-label">
                      <span className="funnel-dot" style={{ background: STAGE_COLORS[s.stage] || 'var(--text-muted)' }} />
                      <span className="funnel-stage">{s.stage}</span>
                    </div>
                    <div className="funnel-bar-wrap">
                      <div
                        className="funnel-bar"
                        style={{
                          width: `${(s.count / maxStageCount) * 100}%`,
                          background: STAGE_COLORS[s.stage] || 'var(--text-muted)',
                          opacity: 1 - i * 0.1,
                        }}
                      />
                    </div>
                    <span className="funnel-count">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */

function StatCard({ label, value, icon, accent, className }) {
  return (
    <div className={`stat-card ${accent ? 'stat-card--accent' : ''} ${className || ''}`}>
      <div className="stat-card-top">
        <div className="stat-card-label">{label}</div>
        <div className="stat-card-icon">{icon}</div>
      </div>
      <div className="stat-card-value">{value}</div>
    </div>
  );
}

function StageBadge({ stage }) {
  const map = {
    'New':         'new',
    'Contacted':   'contact',
    'Site Visit':  'visit',
    'Negotiation': 'negotiation',
    'Closed':      'closed',
    'Lost':        'lost',
  };
  const cls = map[stage] || 'default';
  return (
    <span className={`badge badge-${cls}`}>
      <span className="badge-dot" />
      {stage}
    </span>
  );
}

/* ─── Utils ──────────────────────────────────────────────────────────────── */

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning,';
  if (h < 17) return 'Good afternoon,';
  return 'Good evening,';
}

function formatDate(date) {
  return date.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function timeAgo(isoString) {
  const diff = (Date.now() - new Date(isoString)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/* ─── Icons ──────────────────────────────────────────────────────────────── */

function PlusIcon() {
  return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>;
}
function LeadsIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M1 14c0-2.761 2.239-5 5-5s5 2.239 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M11 7.5h4M13 5.5v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
}
function SparkIcon() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1l1.5 5H15l-4.25 3.09 1.625 5L8 11.18l-4.375 2.91L5.25 9.09 1 6h5.5L8 1Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>;
}
function AlertIcon() {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4"/><path d="M8 5v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="8" cy="11" r="0.75" fill="currentColor"/></svg>;
}
function ClockIcon() {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4"/><path d="M8 4.5V8l2.5 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}
